import React, {useEffect, useRef, useState} from 'react';
import {Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, MenuItem, Select, Switch, CircularProgress, Box, Autocomplete, TextField, useMediaQuery} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import PropTypes from 'prop-types';
import Icon from '@mui/material/Icon';
import SoftBox from "../../../components/SoftBox";
import SoftTypography from "../../../components/SoftTypography";
import SoftInput from "../../../components/SoftInput";
import SoftButton from "../../../components/SoftButton";
import {
    formatMalaysianPhone,
    validateMalaysianPhone,
    validateName,
    validateRequired,
    validateUrl
} from "../../../utils/formValidations";
import {handleApiError, showCreateSuccess, showFormErrors, showUpdateSuccess} from "../../../utils/notificationUtils";
import {USER_STATUS_OPTIONS, USER_STATUS_SELECT_OPTIONS} from "../../../utils/formConstants";
import {useRayzayUIController} from "../../../context";
import {getAllCompanies} from "../../../api/company/companyApiClient";
import {getBranchDetailById} from "../../../api/branch/branchApiClient";

// Timezone options with labels
const TIMEZONE_OPTIONS = [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'Asia/Kuala_Lumpur', label: 'Asia/Kuala_Lumpur (Malaysia Time)' },
    { value: 'Asia/Singapore', label: 'Asia/Singapore (Singapore Time)' },
    { value: 'Asia/Bangkok', label: 'Asia/Bangkok (Thailand Time)' },
    { value: 'Asia/Jakarta', label: 'Asia/Jakarta (Indonesia Western Time)' },
    { value: 'Asia/Makassar', label: 'Asia/Makassar (Indonesia Central Time)' },
    { value: 'Asia/Jayapura', label: 'Asia/Jayapura (Indonesia Eastern Time)' },
    { value: 'Asia/Manila', label: 'Asia/Manila (Philippines Time)' },
    { value: 'Asia/Ho_Chi_Minh', label: 'Asia/Ho_Chi_Minh (Vietnam Time)' },
    { value: 'Asia/Hong_Kong', label: 'Asia/Hong_Kong (Hong Kong Time)' },
    { value: 'Asia/Shanghai', label: 'Asia/Shanghai (China Time)' },
    { value: 'Asia/Taipei', label: 'Asia/Taipei (Taiwan Time)' },
    { value: 'Asia/Seoul', label: 'Asia/Seoul (Korea Time)' },
    { value: 'Asia/Tokyo', label: 'Asia/Tokyo (Japan Time)' },
    { value: 'Asia/Dubai', label: 'Asia/Dubai (UAE Time)' },
    { value: 'Asia/Kolkata', label: 'Asia/Kolkata (India Time)' },
    { value: 'Asia/Dhaka', label: 'Asia/Dhaka (Bangladesh Time)' },
    { value: 'Asia/Karachi', label: 'Asia/Karachi (Pakistan Time)' },
    { value: 'Asia/Tehran', label: 'Asia/Tehran (Iran Time)' },
    { value: 'Asia/Riyadh', label: 'Asia/Riyadh (Saudi Arabia Time)' },
    { value: 'Europe/London', label: 'Europe/London (UK Time)' },
    { value: 'Europe/Paris', label: 'Europe/Paris (Central European Time)' },
    { value: 'Europe/Berlin', label: 'Europe/Berlin (Central European Time)' },
    { value: 'Europe/Rome', label: 'Europe/Rome (Central European Time)' },
    { value: 'Europe/Madrid', label: 'Europe/Madrid (Central European Time)' },
    { value: 'Europe/Amsterdam', label: 'Europe/Amsterdam (Central European Time)' },
    { value: 'Europe/Stockholm', label: 'Europe/Stockholm (Central European Time)' },
    { value: 'Europe/Zurich', label: 'Europe/Zurich (Central European Time)' },
    { value: 'Europe/Vienna', label: 'Europe/Vienna (Central European Time)' },
    { value: 'Europe/Brussels', label: 'Europe/Brussels (Central European Time)' },
    { value: 'America/New_York', label: 'America/New_York (Eastern Time)' },
    { value: 'America/Chicago', label: 'America/Chicago (Central Time)' },
    { value: 'America/Denver', label: 'America/Denver (Mountain Time)' },
    { value: 'America/Los_Angeles', label: 'America/Los_Angeles (Pacific Time)' },
    { value: 'America/Toronto', label: 'America/Toronto (Eastern Time)' },
    { value: 'America/Vancouver', label: 'America/Vancouver (Pacific Time)' },
    { value: 'America/Mexico_City', label: 'America/Mexico_City (Central Time)' },
    { value: 'America/Sao_Paulo', label: 'America/Sao_Paulo (Brazil Time)' },
    { value: 'America/Buenos_Aires', label: 'America/Buenos_Aires (Argentina Time)' },
    { value: 'Australia/Sydney', label: 'Australia/Sydney (Eastern Time)' },
    { value: 'Australia/Melbourne', label: 'Australia/Melbourne (Eastern Time)' },
    { value: 'Australia/Brisbane', label: 'Australia/Brisbane (Eastern Time)' },
    { value: 'Australia/Perth', label: 'Australia/Perth (Western Time)' },
    { value: 'Australia/Adelaide', label: 'Australia/Adelaide (Central Time)' },
    { value: 'Pacific/Auckland', label: 'Pacific/Auckland (New Zealand Time)' },
];

// Move initialFormState outside component to avoid re-creation on every render
const initialFormState = {
    name: '',
    location: '',
    address: '',
    closeTime: '',
    openTime: '',
    phoneNo: '',
    weburl: '',
    timezone: 'Asia/Kuala_Lumpur',
    status: USER_STATUS_OPTIONS.ACTIVE,
    faceDetectionEnabled: true,
    adultDetectionEnabled: false,
    restrictedZoneEnabled: true,
    cameraObstrcutedEnabled: false,
    cameraOfflineEnabled:false,
    companyId: '',
    isActive: true,
};

const AddEditBranchDialog = ({open, onClose, branch, isEdit, onSubmit}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [controller] = useRayzayUIController();
    const {loggedInUserDetail} = controller;
    const isCompanyAdmin = loggedInUserDetail?.userRole === "COMPANY_ADMIN";
    const isBranchAdmin = loggedInUserDetail?.userRole === "BRANCH_ADMIN";
    const [formData, setFormData] = useState(initialFormState);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [companiesError, setCompaniesError] = useState(null);
    const isMountedRef = useRef(true);
    // State to store fetched branch details
    const [branchDetails, setBranchDetails] = useState(null);

    // Fetch branch details if company info is missing or if user is branch admin
    useEffect(() => {
        if (isEdit && branch && open) {
            // Always fetch branch details for branch admin, or if company info is missing
            const needsBranchDetails = isBranchAdmin || (!branch.companyId && !branch.company);
            
            if (needsBranchDetails) {
                const fetchBranchDetails = async () => {
                    try {
                        const branchId = branch.branchId || branch.id;
                        if (branchId) {
                            console.log('Fetching branch details for company info, branchId:', branchId, 'isBranchAdmin:', isBranchAdmin);
                            const response = await getBranchDetailById(branchId);
                            if (response?.data?.branch) {
                                console.log('Fetched branch details:', response.data.branch);
                                setBranchDetails(response.data.branch);
                            }
                        }
                    } catch (error) {
                        console.error('Error fetching branch details:', error);
                    }
                };
                fetchBranchDetails();
            } else {
                setBranchDetails(null);
            }
        } else {
            setBranchDetails(null);
        }
    }, [isEdit, branch, open, isBranchAdmin]);

    useEffect(() => {
        if (!isMountedRef.current) return;

        // Use branchDetails if available, otherwise use branch
        const branchData = branchDetails || branch;

        if (isEdit && branchData) {
            // Get companyId from various possible locations in branch object
            const companyId = branchData.companyId 
                || branchData.company?.companyId 
                || branchData.company?.id
                || branch?.companyId 
                || branch?.company?.companyId 
                || branch?.company?.id 
                || '';
            
            console.log('Setting formData for edit - branchData:', branchData);
            console.log('Extracted companyId:', companyId);
            
            setFormData({
                name: branchData.name || branch.name || '',
                location: branchData.location || branch.location || '',
                address: branchData.address || branch.address || '',
                openTime: branchData.openTime || branch.openTime || '',
                closeTime: branchData.closeTime || branch.closeTime || '',
                phoneNo: branchData.phoneNo || branch.phoneNo || '',
                weburl: branchData.weburl || branch.weburl || '',
                timezone: branchData.timezone || branch.timezone || 'Asia/Kuala_Lumpur',
                //status: branchData.status || branch.status || USER_STATUS_OPTIONS.ACTIVE,
                faceDetectionEnabled: (branchData.faceDetectionEnabled !== undefined && branchData.faceDetectionEnabled !== null)
                    ? branchData.faceDetectionEnabled
                    : (branch.faceDetectionEnabled !== undefined && branch.faceDetectionEnabled !== null ? branch.faceDetectionEnabled : true),
                adultDetectionEnabled: (branchData.adultDetectionEnabled !== undefined && branchData.adultDetectionEnabled !== null)
                    ? branchData.adultDetectionEnabled
                    : (branch.adultDetectionEnabled !== undefined && branch.adultDetectionEnabled !== null ? branch.adultDetectionEnabled : false),
                restrictedZoneEnabled: (branchData.restrictedZoneEnabled !== undefined && branchData.restrictedZoneEnabled !== null)
                    ? branchData.restrictedZoneEnabled
                    : (branch.restrictedZoneEnabled !== undefined && branch.restrictedZoneEnabled !== null ? branch.restrictedZoneEnabled : true),
                companyId: companyId ? String(companyId) : '',
                isActive: branch.isActive !== undefined && branch.isActive !== null ? branch.isActive : true,
            });
        } else if (open) {
            setFormData(initialFormState);
        }

        // Reset states when dialog opens/closes
        if (open) {
            setIsSubmitting(false);
            setErrors({});
        }
    }, [isEdit, branch, branchDetails, open]);

    // Load companies from backend
    const loadCompanies = async () => {
        setLoadingCompanies(true);
        setCompaniesError(null);
        try {
            const response = await getAllCompanies();
            if (response.data && Array.isArray(response.data)) {
                let companiesList = response.data;
                const originalCompaniesList = [...companiesList]; // Keep original for reference
                
                console.log('Loaded companies from API:', companiesList);
                
                // Use branchDetails if available, otherwise use branch
                const branchData = branchDetails || branch;
                
                // Filter to only show active companies (branches can only be created for active companies)
                // When editing, we need to include the branch's company even if inactive
                const branchCompanyId = (isEdit && branchData) ? (
                    branchData.companyId 
                    || branchData.company?.companyId 
                    || branchData.company?.id
                    || branch?.companyId 
                    || branch?.company?.companyId 
                    || branch?.company?.id
                    || null
                ) : null;
                const normalizedBranchCompanyId = branchCompanyId ? String(branchCompanyId) : null;
                
                // Filter to only show active companies, but include the branch's company if editing
                companiesList = companiesList.filter(c => {
                    const cId = String(c.companyId || c.id);
                    const isActive = c.status === USER_STATUS_OPTIONS.ACTIVE;
                    const isBranchCompany = normalizedBranchCompanyId && cId === normalizedBranchCompanyId;
                    // Include if active OR if it's the branch's company (when editing)
                    return isActive || (isEdit && isBranchCompany);
                });
                
                // If editing and branch has company info, ensure it's in the list
                if (isEdit && branchData) {
                    const branchCompanyName = branchData.companyName 
                        || branchData.company?.name
                        || branch?.companyName 
                        || branch?.company?.name;
                    
                    console.log('Branch company info:', { normalizedBranchCompanyId, branchCompanyName, branchData, branch });
                    
                    // Check if branch's company is already in the list
                    const companyExists = companiesList.some(c => {
                        const cId = String(c.companyId || c.id);
                        return cId === normalizedBranchCompanyId;
                    });
                    
                    console.log('Company exists in list:', companyExists);
                    
                    // If editing and user is BRANCH_ADMIN, ensure branch's company is in the list and filter to show only it
                    if (isBranchAdmin && normalizedBranchCompanyId) {
                        // First, ensure the branch's company is in the list
                        let companyName = branchCompanyName;
                        if (!companyName) {
                            // Try to find company name from original API response
                            const originalCompany = originalCompaniesList.find(c => {
                                const cId = String(c.companyId || c.id);
                                return cId === normalizedBranchCompanyId;
                            });
                            companyName = originalCompany?.name || 'Company';
                        }
                        
                        // Check if company exists in the list
                        const companyInList = companiesList.some(c => {
                            const cId = String(c.companyId || c.id);
                            return cId === normalizedBranchCompanyId;
                        });
                        
                        // Add company if not in list (critical for branch admin to see company name)
                        if (!companyInList) {
                            console.log('Adding branch company to list for branch admin:', { normalizedBranchCompanyId, companyName });
                            companiesList.push({
                                companyId: normalizedBranchCompanyId,
                                id: normalizedBranchCompanyId,
                                name: companyName
                            });
                        }
                        
                        // Filter to only show the branch's company in dropdown
                        companiesList = companiesList.filter(c => {
                            const cId = String(c.companyId || c.id);
                            return cId === normalizedBranchCompanyId;
                        });
                        
                        console.log('Branch admin companies list after filtering:', companiesList);
                    } else if (!companyExists && normalizedBranchCompanyId && branchCompanyName) {
                        // For other roles, add branch's company if not in list (only if it was filtered out)
                        // This handles the case where the branch's company is inactive
                        console.log('Adding branch company to list for other roles (may be inactive)');
                        companiesList = [
                            ...companiesList,
                            {
                                companyId: normalizedBranchCompanyId,
                                id: normalizedBranchCompanyId,
                                name: branchCompanyName
                            }
                        ];
                    }
                }
                
                console.log('Final companies list:', companiesList);
                setCompanies(companiesList);
                
                // If editing and branch admin, ensure companyId is set in formData
                if (isEdit && isBranchAdmin && branchData) {
                    const branchCompanyId = branchData.companyId 
                        || branchData.company?.companyId 
                        || branchData.company?.id
                        || branch?.companyId 
                        || branch?.company?.companyId 
                        || branch?.company?.id;
                    
                    if (branchCompanyId && companiesList.length > 0) {
                        const normalizedBranchCompanyId = String(branchCompanyId);
                        setFormData(prev => {
                            // Only update if companyId is not set or doesn't match
                            if (prev.companyId !== normalizedBranchCompanyId) {
                                console.log('Setting companyId for branch admin:', normalizedBranchCompanyId);
                                return {
                                    ...prev,
                                    companyId: normalizedBranchCompanyId
                                };
                            }
                            return prev;
                        });
                    }
                }
                
                // If COMPANY_ADMIN and not editing, auto-select their company
                if (isCompanyAdmin && !isEdit && companiesList.length > 0) {
                    const userCompany = companiesList[0]; // COMPANY_ADMIN only sees their company
                    setFormData(prev => ({
                        ...prev,
                        companyId: String(userCompany.companyId || userCompany.id)
                    }));
                }
            } else {
                setCompaniesError('Invalid company data received');
            }
        } catch (error) {
            console.error('Error loading companies:', error);
            setCompaniesError(error.message || 'Failed to load companies');
        } finally {
            setLoadingCompanies(false);
        }
    };

    useEffect(() => {
        if (open) {
            loadCompanies();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, branchDetails]);

    // Update formData companyId after companies are loaded to ensure proper matching
    useEffect(() => {
        if (isEdit && companies.length > 0) {
            const branchData = branchDetails || branch;
            const branchCompanyId = branchData?.companyId 
                || branchData?.company?.companyId 
                || branchData?.company?.id
                || branch?.companyId 
                || branch?.company?.companyId 
                || branch?.company?.id;
            
            if (branchCompanyId) {
                const normalizedBranchCompanyId = String(branchCompanyId);
                const normalizedFormCompanyId = String(formData.companyId || '');
                
                // If IDs don't match or formData doesn't have companyId, update formData
                if (normalizedFormCompanyId !== normalizedBranchCompanyId) {
                    console.log('Updating companyId in formData to match branch:', normalizedBranchCompanyId);
                    setFormData(prev => ({
                        ...prev,
                        companyId: normalizedBranchCompanyId
                    }));
                }
                
                // Verify the company is in the companies list
                const companyInList = companies.some(c => {
                    const cId = String(c.companyId || c.id);
                    return cId === normalizedBranchCompanyId;
                });
                
                if (!companyInList) {
                    console.warn('Company not found in companies list after load. This should not happen.');
                } else {
                    console.log('Company found in list, should display correctly');
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companies, isEdit, branch, branchDetails]);

    // Cleanup effect
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const handleChange = (e) => {
        setFormData({...formData, [e.target.name]: e.target.value});
    };

    const handleSwitchChange = (e) => {
        setFormData({...formData, [e.target.name]: e.target.checked});
    };

    const handlePhoneChange = (e) => {
        const formattedPhone = formatMalaysianPhone(e.target.value);
        setFormData(prev => ({
            ...prev,
            phoneNo: formattedPhone
        }));
    };

    // Validation function
    const validateForm = () => {
        const newErrors = {};

        // Required field validations
        const nameError = validateName(formData.name, 'Branch name');
        if (nameError) newErrors.name = nameError;

        const locationError = validateRequired(formData.location, 'Location');
        if (locationError) newErrors.location = locationError;

        const addressError = validateRequired(formData.address, 'Address');
        if (addressError) newErrors.address = addressError;

        const phoneError = validateMalaysianPhone(formData.phoneNo);
        if (phoneError) newErrors.phoneNo = phoneError;

        const openTimeError = validateRequired(formData.openTime, 'Open time');
        if (openTimeError) newErrors.openTime = openTimeError;

        const closeTimeError = validateRequired(formData.closeTime, 'Close time');
        if (closeTimeError) newErrors.closeTime = closeTimeError;

        const timezoneError = validateRequired(formData.timezone, 'Timezone');
        if (timezoneError) newErrors.timezone = timezoneError;

        // Company validation (required)
        const companyError = validateRequired(formData.companyId, 'Company');
        if (companyError) newErrors.companyId = companyError;

        // Website URL validation (optional but validate if provided)
        if (formData.weburl && formData.weburl.trim() !== '') {
            const urlError = validateUrl(formData.weburl);
            if (urlError) newErrors.weburl = urlError;
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSubmitting) {
            return; // Prevent double submission
        }

        // Clear previous errors
        setErrors({});

        // Validate form
        const validationErrors = validateForm();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            showFormErrors(validationErrors);
            return;
        }

        setIsSubmitting(true);

        try {
            console.log('Submitting branch data:', formData);
            
            // Submit form
            await onSubmit(formData);

            // Show success message
            if (isEdit) {
                showUpdateSuccess('Branch');
            } else {
                showCreateSuccess('Branch');
            }

            onClose();
        } catch (error) {
            console.error('Branch submission error:', error);
            // Show error notification only (not in dialog)
            handleApiError(error, isEdit ? "Failed to update branch" : "Failed to create branch");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle
          sx={{ borderBottom: `1px solid ${theme.palette.grey[300]}`, bgcolor: "grey.50" }}
        >
          <SoftBox display="flex" justifyContent="space-between" alignItems="center">
            <SoftTypography variant="h5" fontWeight="bold" color="primary">
              {isEdit ? "Edit Branch" : "Add New Branch"}
            </SoftTypography>
          </SoftBox>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <SoftBox mb={0}>
                  <SoftBox mb={1} ml={0.5}>
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Company *
                    </SoftTypography>
                  </SoftBox>
                  <Select
                    name="companyId"
                    value={formData.companyId ? String(formData.companyId) : ""}
                    onChange={handleChange}
                    displayEmpty
                    fullWidth
                    disabled={isCompanyAdmin || isBranchAdmin || loadingCompanies}
                    error={!!errors.companyId}
                    sx={{
                      "& .MuiSelect-select": {
                        padding: "10px 14px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: errors.companyId
                          ? theme.palette.error.main
                          : theme.palette.grey[400],
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: errors.companyId
                          ? theme.palette.error.main
                          : theme.palette.primary.main,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: errors.companyId
                          ? theme.palette.error.main
                          : theme.palette.primary.main,
                      },
                    }}
                  >
                    {loadingCompanies ? (
                      <MenuItem disabled>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={16} />
                          Loading companies...
                        </Box>
                      </MenuItem>
                    ) : companiesError ? (
                      <MenuItem disabled>
                        <SoftTypography color="error">
                          Error loading companies: {companiesError}
                        </SoftTypography>
                      </MenuItem>
                    ) : companies.length === 0 ? (
                      <MenuItem disabled>No companies available</MenuItem>
                    ) : (
                      [
                        <MenuItem key="default" value="" disabled>
                          <em>Please select a company</em>
                        </MenuItem>,
                        ...companies.map((company) => {
                          const companyId = company.companyId || company.id;
                          return (
                            <MenuItem key={companyId} value={String(companyId)}>
                              {company.name}
                            </MenuItem>
                          );
                        }),
                      ]
                    )}
                  </Select>
                  {errors.companyId && (
                    <SoftBox mt={1}>
                      <SoftTypography color="error" variant="caption">
                        {errors.companyId}
                      </SoftTypography>
                    </SoftBox>
                  )}
                  {isCompanyAdmin && (
                    <SoftBox mt={1}>
                      <SoftTypography variant="caption" color="text.secondary">
                        Your company is automatically selected
                      </SoftTypography>
                    </SoftBox>
                  )}
                  {isBranchAdmin && isEdit && (
                    <SoftBox mt={1}>
                      <SoftTypography variant="caption" color="text.secondary">
                        Company cannot be changed
                      </SoftTypography>
                    </SoftBox>
                  )}
                </SoftBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SoftBox mb={0}>
                  <SoftBox mb={1} ml={0.5}>
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Branch Name *
                    </SoftTypography>
                  </SoftBox>
                  <SoftInput
                    name="name"
                    type="text"
                    placeholder="Branch Name"
                    value={formData.name}
                    onChange={handleChange}
                    error={!!errors.name}
                    sx={errors.name ? { borderColor: "error.main" } : {}}
                    required
                  />
                  {errors.name && (
                    <SoftBox mt={1}>
                      <SoftTypography color="error" variant="caption">
                        {errors.name}
                      </SoftTypography>
                    </SoftBox>
                  )}
                </SoftBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SoftBox mb={0}>
                  <SoftBox mb={1} ml={0.5}>
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Location *
                    </SoftTypography>
                  </SoftBox>
                  <SoftInput
                    name="location"
                    type="text"
                    placeholder="Location"
                    value={formData.location}
                    onChange={handleChange}
                    error={!!errors.location}
                    sx={errors.location ? { borderColor: "error.main" } : {}}
                    required
                  />
                  {errors.location && (
                    <SoftBox mt={1}>
                      <SoftTypography color="error" variant="caption">
                        {errors.location}
                      </SoftTypography>
                    </SoftBox>
                  )}
                </SoftBox>
              </Grid>
              <Grid item xs={12} sm={12}>
                <SoftBox mb={0}>
                  <SoftBox mb={1} ml={0.5}>
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Address *
                    </SoftTypography>
                  </SoftBox>
                  <SoftInput
                    name="address"
                    type="text"
                    placeholder="Enter full address"
                    value={formData.address}
                    onChange={handleChange}
                    error={!!errors.address}
                    sx={errors.address ? { borderColor: "error.main" } : {}}
                    required
                  />
                  {errors.address && (
                    <SoftBox mt={1}>
                      <SoftTypography color="error" variant="caption">
                        {errors.address}
                      </SoftTypography>
                    </SoftBox>
                  )}
                </SoftBox>
              </Grid>
              <Grid item xs={12} sm={12}>
                <SoftBox mb={0}>
                  <SoftBox mb={1} ml={0.5}>
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Website URL
                    </SoftTypography>
                  </SoftBox>
                  <SoftInput
                    name="weburl"
                    type="text"
                    placeholder="https://www.example.com"
                    value={formData.weburl}
                    onChange={handleChange}
                    error={!!errors.weburl}
                    sx={errors.weburl ? { borderColor: "error.main" } : {}}
                  />
                  {errors.weburl && (
                    <SoftBox mt={1}>
                      <SoftTypography color="error" variant="caption">
                        {errors.weburl}
                      </SoftTypography>
                    </SoftBox>
                  )}
                </SoftBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SoftBox mb={0}>
                  <SoftBox mb={1} ml={0.5}>
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Phone Number *
                    </SoftTypography>
                  </SoftBox>
                  <SoftInput
                    name="phoneNo"
                    type="tel"
                    placeholder="Enter phone number in E.164 format (e.g., +60133034946)"
                    value={formData.phoneNo}
                    onChange={handlePhoneChange}
                    error={!!errors.phoneNo}
                    sx={errors.phoneNo ? { borderColor: "error.main" } : {}}
                    required
                  />
                  {errors.phoneNo && (
                    <SoftBox mt={1}>
                      <SoftTypography color="error" variant="caption">
                        {errors.phoneNo}
                      </SoftTypography>
                    </SoftBox>
                  )}
                </SoftBox>
              </Grid>
              <Grid item xs={12} sm={3}>
                <SoftBox mb={0}>
                  <SoftBox mb={1} ml={0.5}>
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Open Time *
                    </SoftTypography>
                  </SoftBox>
                  <SoftInput
                    name="openTime"
                    type="time"
                    value={formData.openTime}
                    onChange={handleChange}
                    error={!!errors.openTime}
                    sx={errors.openTime ? { borderColor: "error.main" } : {}}
                    required
                  />
                  {errors.openTime && (
                    <SoftBox mt={1}>
                      <SoftTypography color="error" variant="caption">
                        {errors.openTime}
                      </SoftTypography>
                    </SoftBox>
                  )}
                </SoftBox>
              </Grid>
              <Grid item xs={12} sm={3}>
                <SoftBox mb={0}>
                  <SoftBox mb={1} ml={0.5}>
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Close Time *
                    </SoftTypography>
                  </SoftBox>
                  <SoftInput
                    name="closeTime"
                    type="time"
                    value={formData.closeTime}
                    onChange={handleChange}
                    error={!!errors.closeTime}
                    sx={errors.closeTime ? { borderColor: "error.main" } : {}}
                    required
                  />
                  {errors.closeTime && (
                    <SoftBox mt={1}>
                      <SoftTypography color="error" variant="caption">
                        {errors.closeTime}
                      </SoftTypography>
                    </SoftBox>
                  )}
                </SoftBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SoftBox mb={0}>
                  <SoftBox mb={1} ml={0.5}>
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Timezone *
                    </SoftTypography>
                  </SoftBox>
                  <Autocomplete
                    options={TIMEZONE_OPTIONS}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.label
                    }
                    value={
                      TIMEZONE_OPTIONS.find((tz) => tz.value === formData.timezone) ||
                      TIMEZONE_OPTIONS.find((tz) => tz.value === "Asia/Kuala_Lumpur")
                    }
                    onChange={(event, newValue) => {
                      if (newValue) {
                        handleChange({
                          target: {
                            name: "timezone",
                            value: typeof newValue === "string" ? newValue : newValue.value,
                          },
                        });
                      }
                    }}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="timezone"
                        error={!!errors.timezone}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: errors.timezone
                                ? theme.palette.error.main
                                : theme.palette.grey[400],
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: errors.timezone
                                ? theme.palette.error.main
                                : theme.palette.primary.main,
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: errors.timezone
                                ? theme.palette.error.main
                                : theme.palette.primary.main,
                            },
                          },
                        }}
                      />
                    )}
                    filterOptions={(options, { inputValue }) => {
                      const filter = inputValue.toLowerCase();
                      return options.filter(
                        (option) =>
                          option.label.toLowerCase().includes(filter) ||
                          option.value.toLowerCase().includes(filter)
                      );
                    }}
                  />
                  {errors.timezone && (
                    <SoftBox mt={1}>
                      <SoftTypography color="error" variant="caption">
                        {errors.timezone}
                      </SoftTypography>
                    </SoftBox>
                  )}
                </SoftBox>
              </Grid>
              {/*
              <Grid item xs={12} sm={6}>
                <SoftBox mb={0}>
                  <SoftBox mb={1} ml={0.5}>
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Status *
                    </SoftTypography>
                  </SoftBox>
                  <Select
                    name="status"
                    value={formData.status || USER_STATUS_OPTIONS.ACTIVE}
                    onChange={handleChange}
                    displayEmpty
                    fullWidth
                    sx={{
                      "& .MuiSelect-select": {
                        padding: "10px 14px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.grey[400],
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.primary.main,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    {USER_STATUS_SELECT_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </SoftBox>
              </Grid>
              */}
              <Grid item xs={12} sm={6}>
                <SoftBox mb={0}>
                  <SoftBox mb={1} ml={0.5}>
                    <SoftTypography component="label" variant="caption" fontWeight="bold" display="flex" alignItems="center">
                      <Icon color="info" fontSize="small" sx={{ mr: 0.5 }}>face</Icon>
                      Face Detection
                    </SoftTypography>
                  </SoftBox>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.faceDetectionEnabled}
                        onChange={handleSwitchChange}
                        name="faceDetectionEnabled"
                        color="primary"
                      />
                    }
                    label={
                      <SoftTypography variant="body2" color="text">
                        {formData.faceDetectionEnabled ? "Enabled" : "Disabled"}
                      </SoftTypography>
                    }
                  />
                  <SoftBox mt={1}>
                    <SoftTypography variant="caption" color="text" sx={{ fontSize: "0.75rem" }}>
                      Enable AI-powered face detection for this branch
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                      <SoftBox mb={0}>
                        <SoftBox mb={1} ml={0.5}>
                          <SoftTypography component="label" variant="caption" fontWeight="bold">
                            Branch Status
                          </SoftTypography>
                        </SoftBox>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.isActive}
                              onChange={handleSwitchChange}
                              name="isActive"
                              color="primary"
                            />
                          }
                          label={
                            <SoftTypography variant="body2" color="text">
                              {formData.isActive ? "Active" : "Inactive"}
                            </SoftTypography>
                          }
                        />
                        <SoftBox mt={1}>
                          <SoftTypography variant="caption" color="text" sx={{ fontSize: "0.75rem" }}>
                            Branch status - inactive branches are hidden from dropdowns
                          </SoftTypography>
                        </SoftBox>
                      </SoftBox>
                </Grid>
              {/* Start Group them as Alerts */}
              <Grid item xs={12}>
                <SoftBox mb={2}>
                  <SoftBox display="flex" alignItems="center" mb={2}>
                    <Icon color="info" fontSize="medium" sx={{ mr: 1 }}>notification_important</Icon>
                    <SoftTypography component="label" variant="h6" fontWeight="bold" color="info">
                      Alerts
                    </SoftTypography>
                  </SoftBox>
                  <Grid container spacing={2} direction="row" wrap="wrap">
                    <Grid item xs={12} sm={6} >
                      <SoftBox mb={0}>
                        <SoftBox mb={1} ml={0.5}>
                          <SoftTypography component="label" variant="caption" fontWeight="bold" display="flex" alignItems="center">
                            <Icon color="success" fontSize="small" sx={{ mr: 0.5 }}>shield</Icon>
                            Adult Guard
                          </SoftTypography>
                        </SoftBox>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.adultDetectionEnabled}
                              onChange={handleSwitchChange}
                              name="adultDetectionEnabled"
                            />
                          }
                          label={
                            <SoftTypography variant="body2" color="text">
                              {formData.adultDetectionEnabled ? "Enabled" : "Disabled"}
                            </SoftTypography>
                          }
                        />
                        <SoftBox mt={1}>
                          <SoftTypography variant="caption" color="text" sx={{ fontSize: "0.75rem" }}>
                            Alert branch admins when no adult is detected on included cameras
                          </SoftTypography>
                        </SoftBox>
                      </SoftBox>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SoftBox mb={0}>
                        <SoftBox mb={1} ml={0.5}>
                          <SoftTypography component="label" variant="caption" fontWeight="bold" display="flex" alignItems="center">
                            <Icon color="warning" fontSize="small" sx={{ mr: 0.5 }}>warning</Icon>
                            Restricted Zone
                          </SoftTypography>
                        </SoftBox>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.restrictedZoneEnabled}
                              onChange={handleSwitchChange}
                              name="restrictedZoneEnabled"
                            />
                          }
                          label={
                            <SoftTypography variant="body2" color="text">
                              {formData.restrictedZoneEnabled ? "Enabled" : "Disabled"}
                            </SoftTypography>
                          }
                        />
                        <SoftBox mt={1}>
                          <SoftTypography variant="caption" color="text" sx={{ fontSize: "0.75rem" }}>
                            Alert staff when a child enters a danger zone on this branch&apos;s cameras
                          </SoftTypography>
                        </SoftBox>
                      </SoftBox>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SoftBox mb={0}>
                        <SoftBox mb={1} ml={0.5}>
                          <SoftTypography component="label" variant="caption" fontWeight="bold" display="flex" alignItems="center">
                            <Icon color="error" fontSize="small" sx={{ mr: 0.5 }}>crop_square</Icon>
                            Camera Obstructed
                          </SoftTypography>
                        </SoftBox>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.cameraObstrcutedEnabled}
                              onChange={handleSwitchChange}
                              name="cameraObstructedEnabled"
                            />
                          }
                          label={
                            <SoftTypography variant="body2" color="text">
                              {formData.cameraObstrcutedEnabled ? "Enabled" : "Disabled"}
                            </SoftTypography>
                          }
                        />
                        <SoftBox mt={1}>
                          <SoftTypography variant="caption" color="text" sx={{ fontSize: "0.75rem" }}>
                            Detects when the camera view is blocked by an object, covered lens, or another obstruction.
                          </SoftTypography>
                        </SoftBox>
                      </SoftBox>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SoftBox mb={0}>
                        <SoftBox mb={1} ml={0.5}>
                          <SoftTypography component="label" variant="caption" fontWeight="bold" display="flex" alignItems="center">
                            <Icon color="error" fontSize="small" sx={{ mr: 0.5 }}>wifi_off</Icon>
                            Camera Offline
                          </SoftTypography>
                        </SoftBox>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.cameraOfflineEnabled}
                              onChange={handleSwitchChange}
                              name="cameraOfflineEnabled"
                            />
                          }
                          label={
                            <SoftTypography variant="body2" color="text">
                              {formData.cameraOfflineEnabled ? "Enabled" : "Disabled"}
                            </SoftTypography>
                          }
                        />
                        <SoftBox mt={1}>
                          <SoftTypography variant="caption" color="text" sx={{ fontSize: "0.75rem" }}>
                            Detects a camera that has lost its connection or stopped responding
                          </SoftTypography>
                        </SoftBox>
                      </SoftBox>
                    </Grid>

                  </Grid>
                </SoftBox>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions
            sx={{ p: 3, borderTop: `1px solid ${theme.palette.grey[300]}`, bgcolor: "grey.50" }}
          >
            <SoftButton
              onClick={onClose}
              variant="outlined"
              disabled={isSubmitting}
              sx={{ px: 3, py: 1, borderRadius: 2 }}
            >
              Cancel
            </SoftButton>
            <SoftButton
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              sx={{ px: 3, py: 1, borderRadius: 2 }}
            >
              {isSubmitting
                ? isEdit
                  ? "Updating..."
                  : "Adding..."
                : isEdit
                ? "Update Branch"
                : "Add Branch"}
            </SoftButton>
          </DialogActions>
        </form>
      </Dialog>
    );
};

AddEditBranchDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    branch: PropTypes.object,
    isEdit: PropTypes.bool,
    onSubmit: PropTypes.func.isRequired,
};

export default AddEditBranchDialog; 